import React from 'react';
import { Form, Select } from 'antd';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import { uniqBy } from 'lodash';
import { countryStateCityApi } from 'services/apis';
import { useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import { useTranslation } from 'react-i18next';
import { SortedSelect } from 'components/ui/sorted-select';

interface CountrySelectionProps {
  name?: string;
  label?: string;
  includeCountryFlag?: boolean;
  noFormItem?: boolean;
  countryList?: string[];
  mode?: 'multiple' | 'tags';
  disabled?: boolean;
  includePlaceHolder?: boolean;
  maxTagCount?: number;
}

export const CountrySelection: React.FC<CountrySelectionProps> = ({
  name = 'country',
  label = 'Select country',
  includeCountryFlag = true,
  noFormItem = false,
  countryList,
  mode,
  disabled,
  includePlaceHolder = true,
  maxTagCount,
}) => {
  const { t } = useTranslation();
  const { data: countries, isLoading: isLoadingCountries } = useQuery(
    EQueryKeys.COUNTRIES,
    async () => {
      return countryStateCityApi.getCountries();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      enabled: !countryList, // Only fetch if no custom country list is provided
    }
  );

  const getCountryOptions = () => {
    if (countryList) {
      return countryList.map((countryName) => ({
        value: countryName,
        label: countryName,
        key: countryName,
      }));
    }

    return uniqBy(countries || [], (country) => country.name).map(
      (country) => ({
        value: country.name,
        label: includeCountryFlag
          ? `${country.emoji} ${country.name}`
          : country.name,
        key: country.id,
      })
    );
  };

  const selectComponent = (
    <SortedSelect
      allowClear
      showSearch
      size="large"
      placeholder={
        includePlaceHolder
          ? t('general.selectField', { field: t('general.country') })
          : ''
      }
      loading={!countryList && isLoadingCountries}
      suffixIcon={<ArrowDownIcon />}
      className="w-full"
      options={getCountryOptions()}
      mode={mode}
      disabled={disabled}
      maxTagCount={maxTagCount}
    />
  );

  if (noFormItem) {
    return selectComponent;
  }

  return (
    <Form.Item name={name} label={label} className="mb-4">
      {selectComponent}
    </Form.Item>
  );
};
